import { useMemo, useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { DataGrid, KeenIcon } from '@/components';
import usePagination from '@/hooks/usePagination';
import DateRangePicker from '@/components/inputControls/DateRangePicker';
import moment from 'moment';
import clsx from 'clsx';
import {
  FeedbackResponse,
  FeedbackQueryParams,
  FeedbackType,
  FeedbackStatus,
  ModuleType,
  FeedbackJsmRoutingMode,
  FeedbackJsmRoutingConfigItem,
  FeedbackDestination,
} from '@/types/feedback.types';
import {
  useGetFeedbacksQuery,
  useExportFeedbackCsvQuery,
  useRouteFeedbackMutation,
  useGetFeedbackJsmRoutingConfigQuery,
  useUpdateFeedbackJsmRoutingConfigMutation,
} from '@/services/UserFeedbackService';
import FeedbackDetailsModal from './FeedbackDetailsModal';
import useDebounce from '@/hooks/useDebounce'; // Import the custom useDebounce hook
import { usePermissions } from '@/hooks/usePermissions';
import { formatDisplayDateTime } from '@/utils/Date';

const feedbackTypeMap: Record<
  FeedbackType,
  { label: string; variant: string }
> = {
  [FeedbackType.ASK_QUESTION]: { label: 'Ask Question', variant: 'info' },
  [FeedbackType.LEAVE_COMMENT]: { label: 'Leave Comment', variant: 'warning' },
  [FeedbackType.REPORT_BUG]: { label: 'Report Bug', variant: 'danger' },
  [FeedbackType.SUGGEST_IMPROVEMENT]: {
    label: 'Suggest Improvement',
    variant: 'primary',
  },
  [FeedbackType.GENERAL_FEEDBACK]: {
    label: 'General Feedback',
    variant: 'success',
  },
  [FeedbackType.PRAISE]: { label: 'Praise', variant: 'light' },
};

const feedbackStatusMap: Record<
  FeedbackStatus,
  { label: string; variant: string }
> = {
  [FeedbackStatus.PENDING]: { label: 'Pending', variant: 'warning' },
  [FeedbackStatus.IN_PROGRESS]: { label: 'In Progress', variant: 'primary' },
  [FeedbackStatus.RESOLVED]: { label: 'Resolved', variant: 'success' },
  [FeedbackStatus.CLOSED]: { label: 'Closed', variant: 'info' },
  [FeedbackStatus.REJECTED]: { label: 'Rejected', variant: 'danger' },
};

const moduleTypeMap: Record<ModuleType, string> = {
  [ModuleType.HARBOR_BOOKING]: 'Harbor Booking',
  [ModuleType.POI]: 'Points of Interest',
  [ModuleType.ROUTING]: 'Routing',
  [ModuleType.GENERAL]: 'General',
};

const jsmRoutingModeMap: Record<
  FeedbackJsmRoutingMode,
  { label: string; variant: string }
> = {
  [FeedbackJsmRoutingMode.AUTO]: { label: 'Auto-route', variant: 'success' },
  [FeedbackJsmRoutingMode.MANUAL_REVIEW]: {
    label: 'Manual review',
    variant: 'warning',
  },
};

const UserFeedbackListings = () => {
  const {
    totalPages,
    totalItems,
    handleTotalPages,
    handleTotalItems,
    debouncedValue,
    handleSearch,
    searchTerm,
    pagination,
    setPagination,
    resetSearch,
  } = usePagination();
  const {
    debouncedValue: debouncedUserEmailFilter,
    searchTerm: userEmailFilter,
    handleSearch: handleEmailFilter,
    resetSearch: resetEmailFilter,
  } = useDebounce(1000);
  const [feedbackTypeFilter, setFeedbackTypeFilter] = useState<string>('all');
  const [moduleFilter, setModuleFilter] = useState<string>('all');
  const [statusFilter, setStatusFilter] = useState<string>('all');
  const [routingModeFilter, setRoutingModeFilter] = useState<string>('all');
  const [routingDraft, setRoutingDraft] = useState<
    Record<number, FeedbackJsmRoutingMode>
  >({});
  const [tempStartDate, setTempStartDate] = useState<string>('');
  const [tempEndDate, setTempEndDate] = useState<string>('');
  const [sortBy, setSortBy] = useState<string>('');
  const [exportParams, setExportParams] = useState<FeedbackQueryParams | null>(
    null
  );
  const [modalState, setModalState] = useState<{
    feedbackId: string | null;
    mode: 'edit';
  }>({ feedbackId: null, mode: 'edit' });
  const navigate = useNavigate();
  const { hasPermission } = usePermissions();
  const [routeFeedback, { isLoading: isRouting }] = useRouteFeedbackMutation();
  const { data: routingConfig, isLoading: isRoutingConfigLoading } =
    useGetFeedbackJsmRoutingConfigQuery();
  const [updateRoutingConfig, { isLoading: isSavingRoutingConfig }] =
    useUpdateFeedbackJsmRoutingConfigMutation();

  const queryParams: FeedbackQueryParams = {
    page: pagination.pageIndex + 1,
    limit: pagination.pageSize,
    search: debouncedValue || undefined,
    user_email: debouncedUserEmailFilter || undefined,
    feedback_type:
      feedbackTypeFilter !== 'all'
        ? [parseInt(feedbackTypeFilter) as FeedbackType]
        : undefined,
    module:
      moduleFilter !== 'all'
        ? (parseInt(moduleFilter) as ModuleType)
        : undefined,
    status:
      statusFilter !== 'all'
        ? [parseInt(statusFilter) as FeedbackStatus]
        : undefined,
    jsm_routing_mode:
      routingModeFilter !== 'all'
        ? [parseInt(routingModeFilter) as FeedbackJsmRoutingMode]
        : undefined,
    start_date:
      tempStartDate && moment(tempStartDate, 'DD/MM/YYYY').isValid()
        ? moment(tempStartDate, 'DD/MM/YYYY').format('YYYY-MM-DD')
        : undefined,
    end_date:
      tempEndDate && moment(tempEndDate, 'DD/MM/YYYY').isValid()
        ? moment(tempEndDate, 'DD/MM/YYYY').format('YYYY-MM-DD')
        : undefined,
    sort_by: sortBy || undefined,
  };

  const exportQueryParams: FeedbackQueryParams = {
    search: debouncedValue || undefined,
    user_email: debouncedUserEmailFilter || undefined,
    feedback_type:
      feedbackTypeFilter !== 'all'
        ? [parseInt(feedbackTypeFilter) as FeedbackType]
        : undefined,
    module:
      moduleFilter !== 'all'
        ? (parseInt(moduleFilter) as ModuleType)
        : undefined,
    status:
      statusFilter !== 'all'
        ? [parseInt(statusFilter) as FeedbackStatus]
        : undefined,
    jsm_routing_mode:
      routingModeFilter !== 'all'
        ? [parseInt(routingModeFilter) as FeedbackJsmRoutingMode]
        : undefined,
    start_date:
      tempStartDate && moment(tempStartDate, 'DD/MM/YYYY').isValid()
        ? moment(tempStartDate, 'DD/MM/YYYY').format('YYYY-MM-DD')
        : undefined,
    end_date:
      tempEndDate && moment(tempEndDate, 'DD/MM/YYYY').isValid()
        ? moment(tempEndDate, 'DD/MM/YYYY').format('YYYY-MM-DD')
        : undefined,
    sort_by: sortBy || undefined,
  };

  const {
    data: feedbackResponse,
    isLoading,
    isFetching,
    isError,
    refetch,
  } = useGetFeedbacksQuery(queryParams);

  const { data: exportResponse, isLoading: isExportLoading } =
    useExportFeedbackCsvQuery(exportParams ?? {}, { skip: !exportParams });

  useEffect(() => {
    if (feedbackResponse) {
      handleTotalPages(feedbackResponse.totalPages || 1);
      handleTotalItems(feedbackResponse.totalItems || 0);
      setPagination((prev) => ({
        ...prev,
        pageIndex: (feedbackResponse.currentPage || 1) - 1,
        pageSize: feedbackResponse.pageLimit || prev.pageSize,
      }));
    }
  }, [feedbackResponse, handleTotalPages, handleTotalItems, setPagination]);

  useEffect(() => {
    refetch();
  }, [
    debouncedUserEmailFilter,
    feedbackTypeFilter,
    moduleFilter,
    statusFilter,
    routingModeFilter,
    tempStartDate,
    tempEndDate,
    debouncedValue,
    pagination.pageIndex,
    pagination.pageSize,
    sortBy,
    refetch,
  ]);

  useEffect(() => {
    if (exportResponse?.fileUrl) {
      const link = document.createElement('a');
      link.href = exportResponse?.fileUrl;
      link.download = 'feedback_export.csv';
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      setExportParams(null);
    }
  }, [exportResponse]);

  useEffect(() => {
    if (routingConfig?.length) {
      setRoutingDraft(
        routingConfig.reduce<Record<number, FeedbackJsmRoutingMode>>(
          (acc, item) => {
            acc[item.feedback_type] = item.routing_mode;
            return acc;
          },
          {}
        )
      );
    }
  }, [routingConfig]);

  const hasRoutingConfigChanges = useMemo(() => {
    if (!routingConfig?.length) return false;
    return routingConfig.some(
      (item) => routingDraft[item.feedback_type] !== item.routing_mode
    );
  }, [routingConfig, routingDraft]);

  const handleRoutingConfigSave = async () => {
    if (!routingConfig?.length || !hasRoutingConfigChanges) return;

    try {
      await updateRoutingConfig({
        rules: routingConfig.map((item: FeedbackJsmRoutingConfigItem) => ({
          feedback_type: item.feedback_type,
          routing_mode: routingDraft[item.feedback_type] ?? item.routing_mode,
        })),
      }).unwrap();
    } catch (error) {
      console.warn(error);
    }
  };

  const handleFilterChange = (
    setter: (value: string) => void,
    value: string
  ) => {
    setter(value);
    setPagination((prev) => ({
      ...prev,
      pageIndex: 0,
    }));
  };

  const handleDateRangeChange = (startDate: string, endDate: string) => {
    setTempStartDate(startDate);
    setTempEndDate(endDate);
    setPagination((prev) => ({
      ...prev,
      pageIndex: 0,
    }));
  };

  const resetFilters = () => {
    resetEmailFilter();
    setFeedbackTypeFilter('all');
    setModuleFilter('all');
    setStatusFilter('all');
    setRoutingModeFilter('all');
    setTempStartDate('');
    setTempEndDate('');
    setSortBy('');
    if (searchTerm) {
      resetSearch();
    }
    setPagination((prev) => ({
      ...prev,
      pageIndex: 0,
    }));
  };

  const handleExport = () => {
    setExportParams(exportQueryParams);
  };

  const handleViewFeedback = (id: string) => {
    navigate(`/user-feedbacks/${id}`);
  };

  const handleCloseModal = () => {
    setModalState({ feedbackId: null, mode: 'edit' });
  };

  const handleRouteToSupport = useCallback(
    async (feedback: FeedbackResponse) => {
      try {
        await routeFeedback({
          id: feedback.id,
          payload: {
            destination: FeedbackDestination.JIRA_SERVICE_MANAGEMENT,
            routing_notes:
              'Routed to technical team for investigation and resolution.',
          },
        }).unwrap();
        refetch();
      } catch (error) {
        console.warn(error);
      }
    },
    [refetch, routeFeedback]
  );

  const columns = useMemo(
    () => [
      {
        accessorFn: (row: FeedbackResponse) => row.user.email,
        id: 'user_email',
        header: 'User',
        enableSorting: false,

        cell: ({ row }: { row: { original: FeedbackResponse } }) => {
          return (
            <div className="flex items-center gap-4">
              {row.original.user?.profile_picture ? (
                <img
                  src={row.original.user?.profile_picture}
                  alt={row.original.user?.name || row.original.user?.email}
                  className="rounded-full size-9 shrink-0 object-cover"
                />
              ) : (
                <img
                  src="/media/avatars/defult_avtar.png"
                  alt="Default avatar"
                  className="rounded-full size-9 shrink-0 object-cover"
                />
              )}
              <div className="flex flex-col gap-0.5">
                <span>
                  {row.original.user?.name || row.original.user?.email}
                </span>
                <span className="text-2sm text-gray-700 font-normal">
                  {row.original.user?.roles?.[0]?.display_name || 'Unknown'}
                </span>
              </div>
            </div>
          );
        },
        meta: {
          className: 'min-w-[300px]',
          cellClassName: 'text-gray-800 font-normal',
        },
      },
      {
        accessorFn: (row: FeedbackResponse) => row.created_at,
        id: 'created_at',
        header: () => (
          <div className="flex items-center gap-2">
            <span>Created At</span>
          </div>
        ),
        enableSorting: false,
        cell: ({ row }: { row: { original: FeedbackResponse } }) => {
          return formatDisplayDateTime(row.original.created_at, {
            includeSeconds: true,
          });
        },
        meta: {
          className: 'min-w-[200px]',
          cellClassName: 'text-gray-800 font-normal',
        },
      },
      {
        accessorFn: (row: FeedbackResponse) => row.feedback_type,
        id: 'feedback_type',
        header: 'Feedback Type',
        enableSorting: false,
        cell: ({ row }: { row: { original: FeedbackResponse } }) => {
          const feedback = feedbackTypeMap[row.original.feedback_type] || {
            label: 'Unknown',
            variant: 'light',
          };
          return (
            <span
              className={clsx('badge badge-sm', `badge-${feedback.variant}`)}
              aria-label={`Feedback type: ${feedback.label}`}
            >
              {feedback.label}
            </span>
          );
        },
        meta: {
          className: 'min-w-[180px]',
        },
      },
      {
        accessorFn: (row: FeedbackResponse) => row.created_at,
        id: 'jira_issue_key',
        header: () => (
          <div className="flex items-center gap-2">
            <span>Jira Issue Key</span>
          </div>
        ),
        enableSorting: false,
        cell: ({ row }: { row: { original: FeedbackResponse } }) => {
          return row.original.jira_issue_key ? (
            <span className="text-gray-800 font-normal">
              {row.original.jira_issue_key}
            </span>
          ) : (
            <span className="text-gray-500">-</span>
          );
        },
        meta: {
          className: 'min-w-[200px]',
          cellClassName: 'text-gray-800 font-normal',
        },
      },
      {
        accessorFn: (row: FeedbackResponse) => row.jsm_routing_mode,
        id: 'jsm_routing_mode',
        header: 'JSM Routing',
        enableSorting: false,
        cell: ({ row }: { row: { original: FeedbackResponse } }) => {
          const mode =
            jsmRoutingModeMap[
              row.original.jsm_routing_mode ?? FeedbackJsmRoutingMode.AUTO
            ] || jsmRoutingModeMap[FeedbackJsmRoutingMode.AUTO];
          return (
            <span
              className={clsx('badge badge-sm', `badge-${mode.variant}`)}
              aria-label={`JSM routing: ${mode.label}`}
            >
              {mode.label}
            </span>
          );
        },
        meta: {
          className: 'min-w-[160px]',
        },
      },
      {
        accessorFn: (row: FeedbackResponse) => row.module,
        id: 'module',
        header: 'Module',
        enableSorting: false,
        cell: ({ row }: { row: { original: FeedbackResponse } }) => {
          if (!row.original.module) return <span>-</span>;
          return (
            <span
              className="badge badge-sm badge-light-primary"
              aria-label={`Module: ${moduleTypeMap[row.original.module]}`}
            >
              {moduleTypeMap[row.original.module] || 'Unknown'}
            </span>
          );
        },
        meta: {
          className: 'min-w-[150px]',
        },
      },
      {
        accessorFn: (row: FeedbackResponse) => row.feedback_text,
        id: 'feedback_text',
        header: 'Feedback Content',
        enableSorting: false,
        cell: ({ row }: { row: { original: FeedbackResponse } }) => {
          const content = row.original.feedback_text || 'No content';
          const preview =
            content.length > 50 ? `${content.slice(0, 50)}...` : content;
          return (
            <div className="flex flex-col gap-2">
              <span className="text-gray-800 font-normal">{preview}</span>
            </div>
          );
        },
        meta: {
          className: 'min-w-[300px]',
          cellClassName: 'text-gray-800 font-normal',
        },
      },
      {
        accessorFn: (row: FeedbackResponse) => row.status,
        id: 'status',
        header: 'Status',
        enableSorting: false,
        cell: ({ row }: { row: { original: FeedbackResponse } }) => {
          const status = feedbackStatusMap[row.original.status] || {
            label: 'Unknown',
            variant: 'light',
          };
          return (
            <span
              className={clsx('badge badge-sm', `badge-${status.variant}`)}
              aria-label={`Status: ${status.label}`}
            >
              {feedbackStatusMap[row.original.status]?.label}
            </span>
          );
        },
        meta: {
          className: 'min-w-[150px]',
          cellClassName: 'text-gray-800 font-normal',
        },
      },
      {
        id: 'actions',
        header: 'Actions',
        enableSorting: false,
        cell: ({ row }: { row: { original: FeedbackResponse } }) => (
          <div className="flex gap-2 justify-center">
            {hasPermission('feedback_management_read') && (
              <button
                type="button"
                onClick={() => handleViewFeedback(row.original.id)}
                className="btn btn-sm btn-icon btn-light btn-outline"
                title="View"
                aria-label={`View feedback details for ${row.original.user?.email}`}
              >
                <KeenIcon type="filled" icon="eye" className="text-gray-500" />
              </button>
            )}
            {!row.original.jira_issue_key && (
              <button
                type="button"
                data-testid="keen-icon-double-right-arrow"
                onClick={() => handleRouteToSupport(row.original)}
                className="btn btn-sm btn-icon btn-light btn-outline"
                title="Route to support board"
                aria-label={`Route feedback from ${row.original.user?.email} to support board`}
                disabled={isRouting}
              >
                <KeenIcon
                  type="filled"
                  icon="double-right-arrow -rotate-45"
                  className="text-gray-500"
                />
              </button>
            )}
          </div>
        ),
        meta: {
          className: 'min-w-[100px] text-center',
        },
      },
    ],
    [hasPermission, handleRouteToSupport, isRouting]
  );

  const filteredData: FeedbackResponse[] = useMemo(() => {
    return (feedbackResponse?.data as FeedbackResponse[] | undefined) ?? [];
  }, [feedbackResponse]);

  // Determine if any filters (including search/sort) are currently active
  const hasActiveFilters = useMemo(
    () =>
      Boolean(
        userEmailFilter ||
        feedbackTypeFilter !== 'all' ||
        moduleFilter !== 'all' ||
        statusFilter !== 'all' ||
        routingModeFilter !== 'all' ||
        tempStartDate ||
        tempEndDate ||
        searchTerm ||
        sortBy
      ),
    [
      userEmailFilter,
      feedbackTypeFilter,
      moduleFilter,
      statusFilter,
      routingModeFilter,
      tempStartDate,
      tempEndDate,
      searchTerm,
      sortBy,
    ]
  );

  return (
    <div
      className="card card-grid min-w-full"
      role="region"
      aria-label="Feedback Listings"
    >
      <div className="card-header flex flex-wrap items-center gap-2 sm:gap-4 lg:gap-6 justify-between">
        <div className="flex flex-wrap items-center gap-2 sm:gap-4">
          <div className="flex-shrink-0">
            <label
              className="input input-sm h-8 sm:h-10 flex items-center"
              htmlFor="search-input"
              aria-label="Search feedback by content or user name"
            >
              <KeenIcon
                type="filled"
                icon="magnifier"
                className="text-gray-500"
                aria-hidden="true"
              />
              <input
                id="search-input"
                placeholder="Search feedback or user name"
                type="text"
                value={searchTerm}
                onChange={handleSearch}
                className="w-full sm:w-48 h-8 sm:h-10 text-sm input border-0 scale-y-[0.9] rounded-none"
                aria-label="Search feedback by content or user name"
              />
            </label>
          </div>
          <div className="flex-shrink-0">
            <label
              className="input input-sm h-8 sm:h-10 flex items-center"
              htmlFor="user-email-filter"
              aria-label="Filter feedback by user email"
            >
              <KeenIcon
                type="filled"
                icon="user"
                className="text-gray-500"
                aria-hidden="true"
              />
              <input
                id="user-email-filter"
                placeholder="Filter by User Email"
                type="email"
                value={userEmailFilter}
                autoComplete="email"
                onChange={(e) => handleEmailFilter(e)}
                className="w-full sm:w-48 h-8 sm:h-10 text-sm input border-0 scale-y-[0.9] rounded-none"
                aria-label="Filter feedback by user email"
              />
            </label>
          </div>
          <div className="flex-shrink-0">
            <label htmlFor="sort-by-filter" className="sr-only">
              Sort by
            </label>
            <select
              id="sort-by-filter"
              className="select select-sm w-28 h-8 sm:h-10 text-sm"
              value={sortBy}
              onChange={(e) => handleFilterChange(setSortBy, e.target.value)}
              aria-label="Sort feedback by"
            >
              <option value="">Sort By</option>
              <option value="created_at">Created At</option>
            </select>
          </div>
          <div className="flex-shrink-0">
            <label htmlFor="feedback-type-filter" className="sr-only">
              Filter by feedback type
            </label>
            <select
              id="feedback-type-filter"
              className="select select-sm w-[190px] h-10 text-sm border-gray-200 rounded-lg focus:border-primary focus:ring-1 focus:ring-primary"
              value={feedbackTypeFilter}
              onChange={(e) =>
                handleFilterChange(setFeedbackTypeFilter, e.target.value)
              }
              aria-label="Filter by feedback type"
            >
              <option value="all">All Types</option>
              <option value={FeedbackType.ASK_QUESTION}>Ask Question</option>
              <option value={FeedbackType.LEAVE_COMMENT}>Leave Comment</option>
              <option value={FeedbackType.REPORT_BUG}>Report Bug</option>
              <option value={FeedbackType.SUGGEST_IMPROVEMENT}>
                Suggest Improvement
              </option>
              <option value={FeedbackType.GENERAL_FEEDBACK}>
                General Feedback
              </option>
              <option value={FeedbackType.PRAISE}>Praise</option>
            </select>
          </div>
          <div className="flex-shrink-0">
            <label htmlFor="module-filter" className="sr-only">
              Filter by module
            </label>
            <select
              id="module-filter"
              className="select select-sm w-full h-10 text-sm border-gray-200 rounded-lg focus:border-primary focus:ring-1 focus:ring-primary"
              value={moduleFilter}
              onChange={(e) =>
                handleFilterChange(setModuleFilter, e.target.value)
              }
              aria-label="Filter by module"
            >
              <option value="all">All Modules</option>
              <option value={ModuleType.HARBOR_BOOKING}>Harbor Booking</option>
              <option value={ModuleType.POI}>Points of Interest</option>
              <option value={ModuleType.ROUTING}>Routing</option>
            </select>
          </div>
          <div className="flex-shrink-0">
            <label htmlFor="status-filter" className="sr-only">
              Filter by status
            </label>
            <select
              id="status-filter"
              className="select select-sm w-[130px] h-10 text-sm border-gray-200 rounded-lg focus:border-primary focus:ring-1 focus:ring-primary"
              value={statusFilter}
              onChange={(e) =>
                handleFilterChange(setStatusFilter, e.target.value)
              }
              aria-label="Filter by feedback status"
            >
              <option value="all">All Statuses</option>
              <option value={FeedbackStatus.PENDING}>Pending</option>
              <option value={FeedbackStatus.IN_PROGRESS}>In Progress</option>
              <option value={FeedbackStatus.RESOLVED}>Resolved</option>
              <option value={FeedbackStatus.CLOSED}>Closed</option>
              <option value={FeedbackStatus.REJECTED}>Rejected</option>
            </select>
          </div>
          <div className="flex-shrink-0">
            <label htmlFor="routing-mode-filter" className="sr-only">
              Filter by JSM routing mode
            </label>
            <select
              id="routing-mode-filter"
              className="select select-sm w-[160px] h-10 text-sm border-gray-200 rounded-lg focus:border-primary focus:ring-1 focus:ring-primary"
              value={routingModeFilter}
              onChange={(e) =>
                handleFilterChange(setRoutingModeFilter, e.target.value)
              }
              aria-label="Filter by JSM routing mode"
            >
              <option value="all">All JSM Modes</option>
              <option value={FeedbackJsmRoutingMode.AUTO}>Auto-route</option>
              <option value={FeedbackJsmRoutingMode.MANUAL_REVIEW}>
                Manual Review
              </option>
            </select>
          </div>
          <div className="flex-shrink-0">
            <DateRangePicker
              startDateName="startDate"
              endDateName="endDate"
              isMandatory={false}
              value={[tempStartDate, tempEndDate]}
              onChange={([newStartDate, newEndDate]) => {
                handleDateRangeChange(
                  newStartDate as string,
                  newEndDate as string
                );
              }}
              allowPastDates={true}
              className="w-full h-10 text-sm"
            />
          </div>
        </div>
        <div className="flex items-center gap-3 mt-5">
          {hasActiveFilters && (
            <button
              className="px-4 py-2 bg-gray-200 border border-gray-300 rounded-lg text-sm font-medium text-gray-700 hover:bg-gray-100 transition-colors shadow-sm flex items-center gap-2"
              onClick={resetFilters}
              aria-label="Clear all filters"
              title="Clear all filters"
            >
              <KeenIcon
                type="solid"
                icon="arrows-circle"
                className="text-gray-600"
                aria-hidden="true"
              />
              Clear Filters
            </button>
          )}
          <button
            className="px-4 py-2 bg-primary border border-transparent rounded-lg text-sm font-medium text-white hover:bg-primary-dark transition-colors shadow-sm flex items-center gap-2 disabled:bg-gray-300 disabled:text-gray-500 disabled:cursor-not-allowed"
            onClick={handleExport}
            aria-label="Export filtered feedback as CSV"
            title="Export feedback as CSV"
            disabled={isExportLoading || filteredData.length === 0}
          >
            {isExportLoading ? 'Exporting...' : 'Export CSV'}
          </button>
        </div>
      </div>
      {hasPermission('feedback_management_update') && (
        <div className="px-5 py-4 border-b border-gray-200 bg-gray-100">
          <div className="flex flex-wrap items-end justify-between gap-4">
            <div>
              <h3 className="text-sm font-semibold text-gray-900">
                JSM Routing Rules
              </h3>
              <div className="flex flex-wrap gap-3 mt-3">
                {isRoutingConfigLoading ? (
                  <span className="text-sm text-gray-500">
                    Loading routing rules...
                  </span>
                ) : (
                  routingConfig?.map((item) => (
                    <label
                      key={item.feedback_type}
                      className="flex flex-col gap-1 text-xs font-medium text-gray-600"
                    >
                      {item.feedback_type_label}
                      <select
                        className="select select-sm min-w-[150px] h-9 text-sm"
                        value={
                          routingDraft[item.feedback_type] ?? item.routing_mode
                        }
                        onChange={(e) =>
                          setRoutingDraft((prev) => ({
                            ...prev,
                            [item.feedback_type]: Number(
                              e.target.value
                            ) as FeedbackJsmRoutingMode,
                          }))
                        }
                        aria-label={`${item.feedback_type_label} JSM routing rule`}
                      >
                        <option value={FeedbackJsmRoutingMode.AUTO}>
                          Auto-route
                        </option>
                        <option value={FeedbackJsmRoutingMode.MANUAL_REVIEW}>
                          Manual review
                        </option>
                      </select>
                    </label>
                  ))
                )}
              </div>
            </div>
            <button
              type="button"
              className="btn btn-sm btn-primary"
              onClick={handleRoutingConfigSave}
              disabled={
                !hasRoutingConfigChanges ||
                isSavingRoutingConfig ||
                isRoutingConfigLoading
              }
            >
              {isSavingRoutingConfig ? 'Saving...' : 'Save Rules'}
            </button>
          </div>
        </div>
      )}
      <div className="card-body bg-gray-100 rounded-b-lg shadow-sm">
        {isLoading || isFetching ? (
          <div
            className="text-center py-8 flex flex-col items-center"
            aria-live="polite"
          >
            <div className="w-12 h-12 border-4 border-t-primary border-r-primary border-b-gray-200 border-l-gray-200 rounded-full animate-spin mb-3"></div>
            <p className="text-gray-600">Loading feedback...</p>
          </div>
        ) : isError ? (
          <div
            className="text-center py-8 flex flex-col items-center"
            aria-live="assertive"
          >
            <div className="bg-red-50 p-4 rounded-full mb-3">
              <KeenIcon
                type="solid"
                icon="cross-circle"
                className="w-8 h-8 text-red-500"
                aria-hidden="true"
              />
            </div>
            <p className="text-red-600 font-medium">Failed to load feedback</p>
            <p className="text-gray-500 mt-1">Please try again later</p>
          </div>
        ) : (
          <>
            <DataGrid<FeedbackResponse>
              columns={columns}
              data={filteredData}
              rowSelect={false}
              loading={isLoading || isFetching}
              pagination={{
                page: pagination.pageIndex,
                size: pagination.pageSize,
                more: true,
                totalItems: totalItems,
                totalPages: totalPages,
                setPagination,
                info: `{from} - {to} of {count}`,
                sizes: [5, 10, 25, 50, 100],
                sizesLabel: 'Show',
                sizesDescription: 'per page',
              }}
              sorting={[{ id: 'created_at', desc: true }]}
              serverSide={true}
              onFilterReset={resetFilters}
              hasActiveFilters={hasActiveFilters}
              aria-label="Feedback data grid"
            />
            {modalState.feedbackId && modalState.mode === 'edit' && (
              <FeedbackDetailsModal
                feedbackId={modalState.feedbackId}
                mode={modalState.mode}
                open={!!modalState.feedbackId}
                onClose={handleCloseModal}
              />
            )}
          </>
        )}
      </div>
    </div>
  );
};

export { UserFeedbackListings };
